import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAddedExam } from '../../templates-selector';
import { momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getTemplateDetails } from '../../templates-selector';
import { getTemplateEventColor } from '../../../../common/helpers/common';
import { setEditedExam, setCanAddExam, setAddedExam } from '../../templates-actions';
import { StyledCalendarContainer, StyledCalendar } from '../../../common/styled';
import { ColoredDateCellWrapper } from './configurations/calendar/ColoredDateCellWrapper';
import { CalendarEventWrapper } from './configurations/calendar/CalendarEventWrapper';

const localizer = momentLocalizer(moment);
const today = new Date();

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  item?: any;
  isNew?: boolean;
}

export const Timetable = React.forwardRef((props: any, ref) => {
  const {
    isCreate = false,
    sessionModifiedSlots = [],
    sessionNewSlots = [],
    isClone = false,
    readOnly = false,
  } = props || {};
  const addedExam = useSelector(getAddedExam);
  const dispatch = useDispatch();
  const details = useSelector(getTemplateDetails);
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // Expose a method to remove the 'New Slot' event
  React.useImperativeHandle(ref, () => ({
    removeNewSlot: () => {
      setEvents((prevEvents) => prevEvents.filter((e) => e.title !== 'New Slot'));
    },
    // Add a session-new event immediately so UI highlights as soon as insertion occurs
    addSessionNewEvent: (exam) => {
      if (!exam) return;
      //console.debug('[Timetable] addSessionNewEvent', { examId: exam.examId, examName: exam.examName });
      const todayStr = moment().format('YYYY-MM-DD');
      let start, end;
      if (exam.from && exam.to) {
        start = new Date(`${todayStr} ${exam.from}`);
        end = new Date(`${todayStr} ${exam.to}`);
      } else if (exam.from && exam.durationOfExam) {
        start = new Date(`${todayStr} ${exam.from}`);
        end = new Date(start.getTime() + Number(exam.durationOfExam) * 60000);
      } else {
        start = new Date();
        end = new Date(start.getTime() + 5 * 60000);
      }
      setEvents((prevEvents) => [
        ...prevEvents,
        {
          id: exam.examId,
          title: exam.examName,
          examType: exam.examType,
          start,
          end,
          item: exam,
          isNewSession: true,
        },
      ]);
    },
  }));

  const { components, defaultDate } = useMemo(
    () => ({
      components: {
        timeSlotWrapper: ColoredDateCellWrapper,
        eventWrapper: (props) => <CalendarEventWrapper {...props} readOnly={readOnly} />,
      },
      defaultDate: new Date(),
    }),
    [readOnly]
  );

  useEffect(() => {
    // if (isCreate) {
    //   setEvents([]);
    // } else
    if (details && Array.isArray(details.examsConfiguration)) {
      setEvents(
        details.examsConfiguration.map((item) => {
          const todayStr = moment().format('YYYY-MM-DD');
          let start, end;
          if (item.from && item.to) {
            start = new Date(`${todayStr} ${item.from}`);
            end = new Date(`${todayStr} ${item.to}`);
          } else if (item.from && item.durationOfMinutes && !isNaN(Number(item.durationOfMinutes))) {
            start = new Date(`${todayStr} ${item.from}`);
            end = new Date(start.getTime() + Number(item.durationOfMinutes) * 60000);
          } else if (item.from && item.durationOfExam && !isNaN(Number(item.durationOfExam))) {
            start = new Date(`${todayStr} ${item.from}`);
            end = new Date(start.getTime() + Number(item.durationOfExam) * 60000);
          } else {
            start = new Date();
            end = new Date(start.getTime() + 5 * 60000);
          }
          return {
            id: item.examId,
            title: item.examName,
            examType: item.examType,
            start,
            end,
            item,
            isModified: Array.isArray(sessionModifiedSlots) ? sessionModifiedSlots.includes(item.examId) : false,
            // session-level flag for newly added slots in this edit session
            isNewSession: Array.isArray(sessionNewSlots) ? sessionNewSlots.includes(item.examId) : false,
          };
        })
      );
    }
  }, [details, details?.examsConfiguration, isCreate, sessionModifiedSlots, sessionNewSlots]);

  const eventPropGetter = useCallback((event, start, end, isSelected) => {
    //console.debug('[Timetable] eventPropGetter', { id: event.id, title: event.title, isNew: event.isNew, isNewSession: event.isNewSession, isModified: event.isModified });
    // Use examType for color if present, else fallback to title (examName)
    let backgroundColor = getTemplateEventColor(event.examType || event.title);
    if (event.title === 'New Slot') {
      backgroundColor = '#b7eb8f'; // light green for new slot
    }
    // Prefer black border only when event is explicitly marked modified
    const modifiedBorder = event.isModified ? { border: '1px solid #000000' } : {};
    // Temporary 'New Slot' events or session-new events use solid dark blue border
    const isNewVisual = event.isNew || event.isNewSession;
    const newSlotBorder = isNewVisual ? { border: '1px solid #00008B' } : {};
    // Selection should add a subtle shadow but shouldn't override modified/new borders
    const selectionStyle = isSelected
      ? {
          filter: 'drop-shadow(1px 2px 2px #5A809A7F) contrast(120%)',
        }
      : {};
    return {
      style: {
        backgroundColor,
        // Prefer modified (gold) when event.isModified is set, otherwise use newSlotBorder
        ...(event.isModified ? modifiedBorder : newSlotBorder),
        ...selectionStyle,
      },
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setEditedExam(null));
      dispatch(setCanAddExam(false)); // reset Add button when leaving screen
    };
  }, []);

  // Update the 'New Slot' event if addedExam.durationOfExam changes
  useEffect(() => {
    if (addedExam && addedExam.from && addedExam.durationOfExam) {
      setEvents((prevEvents) => {
        // Only remove temporary 'New Slot' events, not actual inserted exams
        const filtered = prevEvents.filter((e) => !(e.title === 'New Slot'));
        // Parse the start time from addedExam.from
        const todayStr = moment().format('YYYY-MM-DD');
        const start = new Date(`${todayStr} ${addedExam.from}`);
        const end = new Date(start.getTime() + Number(addedExam.durationOfExam) * 60000);
        return [
          ...filtered,
          {
            id: 'new-slot',
            title: 'New Slot',
            start,
            end,
            isNew: true,
          },
        ];
      });
    }
  }, [addedExam]);

  const handleSelectSlot = useCallback(
    (slotInfo) => {
      // Helper to check for conflicts
      const isConflict = (start, end) =>
        events.some(
          (e) =>
            e.title !== 'New Slot' &&
            ((start >= e.start && start < e.end) ||
              (end > e.start && end <= e.end) ||
              (start <= e.start && end >= e.end))
        );

      let start = slotInfo.start;
      let end = new Date(start.getTime() + 5 * 60000); // 5 minutes later
      // If conflict, try next available slot in 5-min steps until 22:00
      while (isConflict(start, end) && end.getHours() < 22) {
        start = new Date(start.getTime() + 5 * 60000);
        end = new Date(start.getTime() + 5 * 60000);
      }
      // If still conflict at end of day, do nothing
      if (isConflict(start, end)) return;

      setEvents((prevEvents) => {
        const filtered = prevEvents.filter((e) => e.title !== 'New Slot');
        const startFormatted = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        const endFormatted = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        dispatch(
          setAddedExam({
            examId: 0,
            examName: 'Exam/CL',
            durationOfExam: 5,
            examType: 'Exam/CL',
            from: startFormatted,
            to: endFormatted,
          })
        );
        return [
          ...filtered,
          {
            id: `new-slot-${Date.now()}`,
            title: 'New Slot',
            start,
            end,
            isNew: true,
          },
        ];
      });
      dispatch(setCanAddExam(true)); // enable Add button
    },
    [dispatch, events]
  );

  return (
    <>
      <StyledCalendarContainer>
        <StyledCalendar
          selectable={!isClone && !readOnly} // disable selection when in clone mode or read-only mode
          components={components}
          defaultDate={defaultDate}
          events={events}
          localizer={localizer}
          step={30}
          defaultView={'day'}
          toolbar={false}
          eventPropGetter={eventPropGetter}
          onSelectSlot={!isClone && !readOnly ? handleSelectSlot : undefined} // prevent click handling when clone or read-only
          min={new Date(today.getFullYear(), today.getMonth(), today.getDate(), 7)}
          max={new Date(today.getFullYear(), today.getMonth(), today.getDate(), 22)}
        />
      </StyledCalendarContainer>
    </>
  );
});
